export interface DependencyNode {
    key: string;
    name: string;
    prerequisites: string[];
}

/** Only inspect the edited object's reachable prerequisites, not unrelated graphs. */
export function findDependencyCycle(nodes: Map<string, DependencyNode>, root: string): string[] | null {
    const active = new Map<string, number>();
    const complete = new Set<string>();
    const path: string[] = [];
    const stack = [{ key: root, next: 0 }];
    while (stack.length) {
        const frame = stack[stack.length - 1];
        if (!active.has(frame.key)) {
            active.set(frame.key, path.length);
            path.push(frame.key);
        }
        const edges = nodes.get(frame.key)?.prerequisites ?? [];
        if (frame.next === edges.length) {
            complete.add(frame.key);
            active.delete(frame.key);
            path.pop();
            stack.pop();
            continue;
        }
        const next = edges[frame.next++];
        const index = active.get(next);
        if (index !== undefined) return [...path.slice(index), next];
        if (!complete.has(next)) stack.push({ key: next, next: 0 });
    }
    return null;
}

export function dependencyCycleMessage(nodes: Map<string, DependencyNode>, cycle: string[]): string {
    return 'Circular dependency: ' + cycle.map(key => nodes.get(key)?.name || key || 'New item').join(' → ') + '.';
}

/** Read fresh prerequisites before saving, replacing the edited node with its draft. */
export async function checkDependencyCycle(
    draft: DependencyNode,
    load: (key: string) => Promise<DependencyNode | null>,
): Promise<string | null> {
    const nodes = new Map([[draft.key, draft]]);
    const queue = [...draft.prerequisites];
    for (let index = 0; index < queue.length; index++) {
        const key = queue[index];
        if (nodes.has(key)) continue;
        const node = await load(key);
        if (!node || node.key !== key) throw new Error('Could not verify prerequisites. Reload and try saving again.');
        nodes.set(key, node);
        queue.push(...node.prerequisites);
    }
    const cycle = findDependencyCycle(nodes, draft.key);
    return cycle ? dependencyCycleMessage(nodes, cycle) : null;
}
